import { useContext, useEffect, useState } from "react";
import SlotDetailCellRenderer from "./SlotDetailCellRenderer";
import {OwcChip, OwcGrid, OwcTooltip, OwcTypography} from "@one/react";
import {
  valueOrEmpty,
  formatUserDisplay,
  USER_FORMAT_PRESETS,
} from "../../utils/helpers/text";
import DLabGrid from "../DLabGrid/DLabGrid";
import { changeActionTypeToDisplay, isActionType } from "./helpFunctions";
import InlineEditableField from "./InlineEditableField/InlineEditableField";
import { QUICK_EDIT_BOOKING } from "../../gql/bookingapi/mutations";
import { useSelector } from "react-redux";
import { quickEditTracker } from "../../features/booking-operations/quickEditTracker";
import { withApollo, compose } from "react-apollo";
import { BookingMeta } from "../../constants";
import { ElnLinkRenderer } from "@digitallab/grid-common-components";
import HazardousApprovalActions from "../../features/hazardous-materials/HazardousApprovalActions";
import AssistanceDetails from "./CommonPopOverModal/BookingEventDetails/AssistanceDetails";
import DateTimeRange from "./CommonPopOverModal/BookingEventDetails/DateTimeRange";
import { projectDisplayText } from "../../features/user/redux/selectors";
import { PaginationContext } from "./pagination/PaginationContext";
import ChecklistRender from "../../features/equipment-list/ChecklistRender";

const BookingDetailRenderer = ({
  data,
  client,
  isAdminContext = false,
  displayPreset,
  setOpenHazardousRejectDialog,
  setHazardousBookingToReject,
  setOpenAssistanceDialog,
  setBookingForAssistance,
  setIsEditMode,
  allowAssistanceEdit = false,
  allowDescriptionEdit = true,
  onDescriptionUpdate,
  onHazardousMaterialsUpdate,
  onAssistanceUpdateComplete,
  status,
  setStatus,
}) => {
  // eslint-disable-next-line no-unused-vars
  const [rowData] = useState(data?.bookingEntries ?? []);
  const usersList = useSelector((state) => state.users.users);
  const [descriptionValue, setDescriptionValue] = useState(data?.description ?? "");
  const [hazardousMaterialsValue, setHazardousMaterialsValue] = useState(
    data?.hazardousDeclaration?.hazardousMaterialsList ?? "",
  );
  const user = useSelector((store) => store.user);
  const projectLabel = useSelector((state) =>
    projectDisplayText(state, data?.redPandaProjects, data?.project)
  );
  const isBookingAdmin = user?.roles?.some((role) => role === "BookingAdmin");
  const canEditDescription =
    allowDescriptionEdit && (isAdminContext || isBookingAdmin);
  const rowStyle = { background: "var(--one-color-cobas-accent-gray-100)" };
  const { dispatchAction } = useContext(PaginationContext);
  const ALL_COLUMNS = {
    Model: {
      field: "Model",
      headerName: "Model",
      valueFormatter: (row) =>
        valueOrEmpty(row?.data?.equipment?.equipmentModel, false, "-"),
    },
    serialNumber: {
      field: "SerialNumber",
      headerName: "Serial number",
      valueFormatter: (row) =>
        valueOrEmpty(row?.data?.equipment?.serialNumber, false, "-"),
    },
    equipmentId: {
      field: "Equipment ID",
      headerName: "Equipment ID",
      valueFormatter: (row) =>
        valueOrEmpty(row?.data?.equipment?.equipmentId, false, "-"),
    },
    equipmentNickName: {
      field: "Nickname",
      headerName: "Nickname",
      valueFormatter: (row) =>
        valueOrEmpty(row?.data?.equipment?.equipmentNickName, false, "-"),
    },
    location: {
      field: "Location",
      headerName: "Location",
      valueFormatter: (row) =>
        valueOrEmpty(row?.data?.equipment?.location, false, "-"),
    },
    softwareVersion: {
      field: "Software version",
      headerName: "Software version",
      valueFormatter: (row) =>
        valueOrEmpty(row?.data?.equipment?.softwareVersion, false, "-"),
    },
    belongingToGroup: {
      field: "Group",
      headerName: "Group",
      valueFormatter: (row) =>
        valueOrEmpty(row?.data?.equipment?.belongingToGroup, false, "-"),
    },
    checklists: {
      field: "checklists",
      headerName: "Checklists",
      cellRenderer: ChecklistRender,
      width: 60,
      suppressSizeToFit: true,
    },
  };

  const buildColumnDefs = (preset) => {
    const base = [
      {
        field: "",
        cellRenderer: "agGroupCellRenderer",
        width: 80,
        pinned: "left",
      },
    ];
    return [...base, ...Object.keys(ALL_COLUMNS).map((c) => ALL_COLUMNS[c])];
  };

  const [columnDefs, setColumnDefs] = useState(() =>
    buildColumnDefs(displayPreset),
  );

  useEffect(() => {
    const newDefs = buildColumnDefs(displayPreset);
    setColumnDefs(newDefs); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayPreset]);

  useEffect(() => {
    setDescriptionValue(data?.description ?? "");
  }, [data?.description]);

  useEffect(() => {
    setHazardousMaterialsValue(
      data?.hazardousDeclaration?.hazardousMaterialsList ?? "",
    );
  }, [data?.hazardousDeclaration?.hazardousMaterialsList]);

  const handleDescriptionSave = async (nextValue) => {
    const trimmed = (nextValue ?? "").trim();
    const current = (descriptionValue ?? "").trim();
    if (trimmed === current) {
      return;
    }
    const bookingId = data?.bookingId || data?.id;

    dispatchAction({ type: "fetching", payload: true });
    if (setStatus) setStatus(true);

    const result = await client.mutate({
      mutation: QUICK_EDIT_BOOKING,
      fetchPolicy: "no-cache",
      variables: {
        input: {
          bookingId,
          description: trimmed,
          createdByEmail: user?.email,
        },
      },
    });
    const payload = result?.data?.quickEditBooking;
    if (!payload?.success) {
      dispatchAction({ type: "fetching", payload: false });
      if (setStatus) setStatus(false);
      throw new Error(payload?.message || "Description update failed");
    }

    setDescriptionValue(trimmed);

    if (payload?.commandId) {
      quickEditTracker.register(payload.commandId, "UPDATE", {
        onSuccess: () => {
          dispatchAction({ type: "fetching", payload: false });
          if (setStatus) setStatus(false);
          if (onDescriptionUpdate) {
            onDescriptionUpdate(trimmed);
          }
        },
        onError: () => {
          dispatchAction({ type: "fetching", payload: false });
          if (setStatus) setStatus(false);
        },
      });
    } else {
      dispatchAction({ type: "fetching", payload: false });
      if (setStatus) setStatus(false);
      if (onDescriptionUpdate) {
        await onDescriptionUpdate(trimmed);
      }
    }
  };

  const defaultColDef = {
    flex: 1,
    resizable: true,
    suppressMenu: true,
  };

  let attr = {
    columnDefs,
    animateRows: true,
    rowExport: false,
    defaultToolPanel: "filters",
    suppressContextMenu: true,
    hiddenByDefault: true,
    rowData: rowData,
  };

  const gridItemStyle = { alignSelf: "start", maxWidth: '483.33px', display: 'block' };
  const projectLabelStyle = { maxWidth: "340px", display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };

  return (
    <div
      style={{
        padding: "0.625rem 2rem 1rem 2rem",
        backgroundColor: "#FBFBFA",
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
      }}
    >
      <OwcGrid container columns={3} vGutter={24} hGutter={1}>
        <OwcGrid item xl={1} l={1} m={1} xs={3} s={3} style={gridItemStyle}>
          <div
            style={{
              display: "flex",
              gap: "1.5rem",
              flexDirection: "column",
              flexWrap: "nowrap",
            }}
          >
            {/* Date and time range */}
            <div >
              <DateTimeRange
                start={data?.dateFrom}
                end={data?.dateTo}
                timezone={user?.timezone}
                repetitionPattern={data?.repetitionPattern}
              />
            </div>

            {/* Description*/}
            <div style={{ minWidth: "fit-content" }}>
              <InlineEditableField
                maxDisplayLength={48} // 45 characters max makes sense here becaouse Date and Time range fomrat is 45 characters. 45 characters + 3 dots = 48 characters
                label="Description"
                value={descriptionValue}
                allowEdit={canEditDescription}
                placeholder="Enter description"
                onSave={handleDescriptionSave}
                displayFormatter={(val) => valueOrEmpty(val, false, "-")}
              />
            </div>

            {/* Project */}
            <div>
              <OwcTypography variant="caption">
                {data?.redPandaProjects?.length ? 'Projects' : 'Project'}
              </OwcTypography>
              <OwcTypography
                variant="subtitle2"
                style={projectLabelStyle}
                title={projectLabel || ''}
                id={`project_${data?.id ?? "row"}`}>
                {projectLabel}
              </OwcTypography>
              {!!projectLabel && (
                <OwcTooltip anchor={`project_${data?.id ?? "row"}`}>
                  <span style={{ whiteSpace: "pre-line" }}>
                    {projectLabel.replaceAll(", ", "\n")}
                  </span>
                </OwcTooltip>
              )}
            </div>
          </div>
        </OwcGrid>

        <OwcGrid item xl={1} l={1} m={1} xs={3} s={3} style={gridItemStyle}>
          <div
            style={{ display: "flex", gap: "1.5rem", flexDirection: "column" }}
          >
            {/* eLN ID or Action type */}
            {isActionType(data?.type) ? (
              <div>
                <OwcTypography variant="caption">Action type</OwcTypography>
                <OwcTypography variant="subtitle2">
                  {valueOrEmpty(
                    changeActionTypeToDisplay(data?.type),
                    false,
                    "-",
                  )}
                </OwcTypography>
              </div>
            ) : (
              <div>
                <OwcTypography variant="caption">eLN ID</OwcTypography>
                <OwcTypography variant="subtitle2">
                  <ElnLinkRenderer elnExperiment={data?.elnId} />
                </OwcTypography>
              </div>
            )}

            {/* Approval status */}
            <div>
              <OwcTypography variant="caption">
                {BookingMeta.fields.hazardous.text}
              </OwcTypography>
              <HazardousApprovalActions
                booking={data}
                setOpenHazardousRejectDialog={setOpenHazardousRejectDialog}
                setHazardousBookingToReject={setHazardousBookingToReject}
                setOpenAssistanceDialog={setOpenAssistanceDialog}
                setBookingForAssistance={setBookingForAssistance}
                setIsEditMode={setIsEditMode}
              />
            </div>

            {/* Reason for rejection, show only if booking is rejected */}
            {data?.approval?.status === "REJECTED" && (
              <div>
                <OwcTypography variant="caption">
                  Reason for rejection
                </OwcTypography>
                <OwcTypography variant="subtitle2">
                  {valueOrEmpty(data?.approval?.rejectionReason, false, "-")}
                </OwcTypography>
              </div>
            )}
          </div>
        </OwcGrid>

        <OwcGrid item xl={1} l={1} m={1} xs={3} s={3} style={gridItemStyle}>
          <div
            style={{ display: "flex", gap: "1.5rem", flexDirection: "column" }}
          >
            {/* Hazardous materials (editable) */}
            <div>
              <InlineEditableField
                label="Hazardous materials details"
                value={hazardousMaterialsValue}
                allowEdit={
                  canEditDescription &&
                  !!data?.hazardousDeclaration?.hasHazardousMaterials
                }
                placeholder="Enter hazardous materials list"
                onSave={async (nextVal) => {
                  const bookingId = data?.bookingId || data?.id;
                  if (!bookingId) return;
                  const trimmed = (nextVal || "").trim();
                  const current = (
                    data?.hazardousDeclaration?.hazardousMaterialsList || ""
                  ).trim();
                  if (trimmed === current) return;

                  dispatchAction({ type: "fetching", payload: true });
                  if (setStatus) setStatus(true);

                  const result = await client.mutate({
                    mutation: QUICK_EDIT_BOOKING,
                    fetchPolicy: "no-cache",
                    variables: {
                      input: {
                        bookingId,
                        hazardousDeclaration: {
                          hasHazardousMaterials:
                            !!data?.hazardousDeclaration?.hasHazardousMaterials,
                          hazardousMaterialsList: trimmed,
                        },
                        createdByEmail: user.email,
                      },
                    },
                  });
                  const payload = result?.data?.quickEditBooking;
                  if (!payload?.success) {
                    dispatchAction({ type: "fetching", payload: false });
                    if (setStatus) setStatus(false);
                    throw new Error(
                      payload?.message || "Hazardous materials update failed",
                    );
                  }

                  if (payload?.commandId) {
                    quickEditTracker.register(payload.commandId, "UPDATE", {
                      onSuccess: () => {
                        dispatchAction({ type: "fetching", payload: false });
                        if (setStatus) setStatus(false);
                        if (onHazardousMaterialsUpdate) {
                          onHazardousMaterialsUpdate(trimmed);
                        }
                      },
                      onError: () => {
                        dispatchAction({ type: "fetching", payload: false });
                        if (setStatus) setStatus(false);
                      },
                    });
                  } else {
                    dispatchAction({ type: "fetching", payload: false });
                    if (setStatus) setStatus(false);
                    if (onHazardousMaterialsUpdate) {
                      await onHazardousMaterialsUpdate(trimmed);
                    }
                  }
                }}
                displayFormatter={(val) => valueOrEmpty(val, false, "-")}
              />
            </div>

            {/* Assistance details  */}
            <div>
              <AssistanceDetails
                assistanceDeclaration={data?.assistanceDeclaration}
                assistanceDuration={data?.assistentTime}
                assistantName={(() => {
                  if (!data?.assistent || !data?.assistent[0]) return null;
                  const assistantId = data.assistent[0];
                  const assistantUser = usersList?.find(
                    (user) => user.userId === assistantId,
                  );
                  if (assistantUser) {
                    return formatUserDisplay(
                      assistantUser,
                      USER_FORMAT_PRESETS.nameOnly,
                    );
                  }
                  return assistantId;
                })()}
                approvalStatus={data?.approval?.status}
                booking={data}
                client={client}
                allowEdit={
                  allowAssistanceEdit && isAdminContext && isBookingAdmin
                }
                setStatus={setStatus}
                dispatchAction={dispatchAction}
                onAssistanceUpdateComplete={onAssistanceUpdateComplete}
                onClick={() => {
                  if (
                    setBookingForAssistance &&
                    setOpenAssistanceDialog &&
                    setIsEditMode
                  ) {
                    setBookingForAssistance(data);
                    setIsEditMode(true);
                    setOpenAssistanceDialog(true);
                  }
                }}
              />
            </div>
          </div>
        </OwcGrid>

        <OwcGrid item xl={3} l={3} m={3} xs={3} s={3} style={gridItemStyle}>
          <div>
            <OwcTypography variant="caption">Booking for</OwcTypography>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              {data.bookedUsers?.map((x) => (
                <OwcChip icon={"user"} iconType="outlined" outlined round>
                  {x
                    ? `${x?.givenName ?? ""}  ${x?.familyName ?? ""}  (${x?.name ?? x?.email})`
                    : "-"}
                </OwcChip>
              ))}
            </div>
          </div>
        </OwcGrid>
      </OwcGrid>

      {/* Equipment details */}
      <div style={{ backgroundColor: "#FFFFFF" }}>
        <div
          style={{
            paddingTop: ".5rem",
          }}
        >
          <OwcTypography
            variant="button"
            style={{
              paddingTop: ".625rem",
              paddingBottom: ".625rem",
              paddingRight: ".5rem",
              paddingLeft: "1rem",
            }}
          >
            Equipment details
          </OwcTypography>
        </div>
        <div
          style={{
            paddingRight: "1rem",
            paddingLeft: "1rem",
          }}
        >
          <div className="ag-theme-alpine">
            <div className="sub-table-grid">
              <DLabGrid
                {...attr}
                pagination={false}
                suppressPaginationPanel={false}
                defaultColDef={defaultColDef}
                masterDetail={true}
                detailRowAutoHeight={true}
                detailCellRenderer={SlotDetailCellRenderer}
                autoGroupColumnDef={"autoGroupColumnDef"}
                rowStyle={rowStyle}
                domLayout={"autoHeight"}
                height={"100%"}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default compose(withApollo)(BookingDetailRenderer);
